import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const OrgChatAI = () => {
    const { t } = useTranslation();
    const { mode } = useTheme();
    const navigate = useNavigate();

    const features = [
        t('smartOrg.orgchat.features.item1'),
        t('smartOrg.orgchat.features.item2'),
        t('smartOrg.orgchat.features.item3'),
        t('smartOrg.orgchat.features.item4'),
        t('smartOrg.orgchat.features.item5'),
    ];

    const advantages = [
        t('smartOrg.orgchat.advantages.item1'),
        t('smartOrg.orgchat.advantages.item2'),
        t('smartOrg.orgchat.advantages.item3'),
        t('smartOrg.orgchat.advantages.item4'),
        t('smartOrg.orgchat.advantages.item5'),
    ];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: mode === 'dark'
                    ? 'linear-gradient(135deg, #1e3a5f 0%, #0f2642 100%)'
                    : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                py: { xs: 10, md: 12 },
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="xl">
                {/* Back Button */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        onClick={() => navigate(-1)}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            color: mode === 'dark' ? '#ffffff' : '#1e293b',
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                background: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            },
                        }}
                    >
                        {t('common.back')}
                    </Button>
                </Box>

                {/* Title */}
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '4rem' },
                            fontWeight: 'bold',
                            color: '#f97316',
                        }}
                    >
                        {t('smartOrg.orgchat.title')}
                    </Typography>
                </Box>

                {/* Features and Advantages Section */}
                <Grid container spacing={4} sx={{ mb: { xs: 6, md: 8 } }}>
                    {/* Features Section */}
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 'bold',
                                color: mode === 'dark' ? '#ffffff' : '#1e293b',
                                mb: 3,
                            }}
                        >
                            Features
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {features.map((feature, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 8,
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: '#f97316',
                                            mt: 1,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '1rem', md: '1.125rem' },
                                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                            lineHeight: 1.8,
                                        }}
                                    >
                                        {feature}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* Advantages Section */}
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 'bold',
                                color: mode === 'dark' ? '#ffffff' : '#1e293b',
                                mb: 3,
                            }}
                        >
                            Advantages
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {advantages.map((advantage, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 8,
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: '#f97316',
                                            mt: 1,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '1rem', md: '1.125rem' },
                                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                            lineHeight: 1.8,
                                        }}
                                    >
                                        {advantage}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>

                {/* Images Row - Two Images Side by Side */}
                <Grid container spacing={4}>
                    {/* First Image */}
                    <Grid item xs={12} md={6}>
                        <Box
                            component="img"
                            src="/assets/images/Al-chat.png"
                            alt="OrgChat AI Preview 1"
                            sx={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                imageRendering: 'high-quality',
                                borderRadius: 3,
                                boxShadow: mode === 'dark'
                                    ? '0 10px 40px rgba(249, 115, 22, 0.3)'
                                    : '0 10px 40px rgba(0, 0, 0, 0.15)',
                            }}
                        />
                    </Grid>

                    {/* Second Image */}
                    <Grid item xs={12} md={6}>
                        <Box
                            component="img"
                            src="/assets/images/Al-chat2.png"
                            alt="OrgChat AI Preview 2"
                            sx={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                imageRendering: 'high-quality',
                                borderRadius: 3,
                                boxShadow: mode === 'dark'
                                    ? '0 10px 40px rgba(249, 115, 22, 0.3)'
                                    : '0 10px 40px rgba(0, 0, 0, 0.15)',
                            }}
                        />
                    </Grid>
                </Grid>

                {/* Decorative Elements */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 100,
                        left: 50,
                        width: 40,
                        height: 40,
                        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                        background: mode === 'dark'
                            ? 'rgba(249, 115, 22, 0.2)'
                            : 'rgba(249, 115, 22, 0.15)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 200,
                        right: 80,
                        width: 50,
                        height: 50,
                        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                        background: mode === 'dark'
                            ? 'rgba(249, 115, 22, 0.15)'
                            : 'rgba(249, 115, 22, 0.1)',
                    }}
                />
            </Container>
        </Box>
    );
};

export default OrgChatAI;